import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUser } from './UserContext';
import { getAchievements as fetchAchievements, getAchievementStats, checkAllAchievements } from '../services/api';
import { showToast } from '../components/GlobalToast';

const AchievementsContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAchievements = () => {
  const ctx = useContext(AchievementsContext);
  if (!ctx) throw new Error('useAchievements must be used within AchievementsProvider');
  return ctx;
};

export const AchievementsProvider = ({ children }) => {
  const { user } = useUser();
  
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState({ total: 0, unlocked: 0, progress_pct: 0, recent_unlocked: [] });
  const [loading, setLoading] = useState(false);

  const loadAchievements = useCallback(async () => {
    if (!user?.id) {
      setAchievements([]);
      setStats({ total: 0, unlocked: 0, progress_pct: 0, recent_unlocked: [] });
      return;
    }

    try {
      setLoading(true);
      const [achData, statsData] = await Promise.all([
        fetchAchievements(),
        getAchievementStats()
      ]);
      setAchievements(achData || []);
      setStats(statsData || { total: 0, unlocked: 0, progress_pct: 0, recent_unlocked: [] });
    } catch (error) {
      console.error('Ошибка загрузки достижений:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Загружаем при монтировании и смене пользователя
  useEffect(() => {
    loadAchievements();
  }, [loadAchievements]);

  // Проверить все достижения
  const checkAll = useCallback(async () => {
    if (!user?.id) return;
    try {
      const result = await checkAllAchievements();
      if (result?.newly_unlocked?.length > 0) {
        // Показываем тост о новых достижениях
        result.newly_unlocked.forEach(ach => {
          showToast({
            type: 'achievement',
            title: `🏆 Новое достижение: ${ach.title}`,
            message: ach.description,
            duration: 5000
          });
        });
      }
      // Перезагружаем после проверки
      await loadAchievements();
      return result;
    } catch (error) {
      console.error('Ошибка проверки достижений:', error);
    }
  }, [user, loadAchievements]);

  // Получить достижения по категории
  const getAchievementsByCategory = useCallback((category) => {
    return achievements.filter(a => a.category === category);
  }, [achievements]);

  // Получить количество открытых достижений
  const getUnlockedCount = useCallback(() => {
    return achievements.filter(a => a.is_unlocked).length;
  }, [achievements]);

  const value = {
    achievements,
    stats,
    loading,
    loadAchievements,
    checkAll,
    getAchievementsByCategory,
    getUnlockedCount
  };

  return (
    <AchievementsContext.Provider value={value}>
      {children}
    </AchievementsContext.Provider>
  );
};

export default AchievementsContext;